import { useEffect, useState } from 'react';
import { StatusEnum } from '../../../../common/data/base/enums/statusEnum';
import MESSAGES from '../../../../common/data/constants/messages';
import { IJobTitle } from '../../../../common/data/dtos/job-title';
import { ITraining } from '../../../../common/data/dtos/training';
import { ITrainingJobTitle } from '../../../../common/data/dtos/training-job-title';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardFooterRight, CardTabItem } from '../../../../components/bootstrap/Card';
import FormGroup from '../../../../components/bootstrap/forms/FormGroup';
import InputGroup from '../../../../components/bootstrap/forms/InputGroup';
import Select from '../../../../components/bootstrap/forms/Select';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../../components/bootstrap/Modal';
import Spinner from '../../../../components/bootstrap/Spinner';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import trainingJobTitleService from '../../../../services/training-job-title-service';
import trainingService from '../../../../services/training-service';

interface Props {
    isModalOpened: boolean;
    selectedJobTitle: IJobTitle | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const TrainingJobTitleModal = ({ isModalOpened, selectedJobTitle, onModalClose, onAfterSubmit }: Props) => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [trainings, setTrainings] = useState<ITraining[]>([]);
    const [trainingJobTitles, setTrainingJobTitles] = useState<ITrainingJobTitle[]>([]);
    const [selectedTrainingId, setSelectedTrainingId] = useState<string>('');

    useEffect(() => {
        if (selectedJobTitle) {
            fetchData();
            setSelectedTrainingId('');
        }
    }, [isModalOpened]);

    const fetchData = async () => {
        setIsSubmitting(true);

        try {
            if (!selectedJobTitle?.id) {
                showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            } else {
                const [trainingsResponse, trainingJobTitleResponse] = await Promise.all([trainingService.getTrainings(), trainingJobTitleService.getTrainingJobTitleByJobTitle(selectedJobTitle.id)]);

                if (trainingsResponse.error) {
                    showWarning(trainingsResponse.error);
                    return;
                }

                if (trainingJobTitleResponse.error) {
                    showWarning(trainingJobTitleResponse.error);
                    return;
                }

                setTrainings(trainingsResponse.trainings || []);
                setTrainingJobTitles(trainingJobTitleResponse.trainingJobTitles || []);
            }
        } catch (error) {
            console.error('Erro nas requisições:', error);
            showWarning('Erro ao carregar dados');
        } finally {
            setIsSubmitting(false);
        }
    };

    const fetchTrainingJobTitle = async () => {
        if (selectedJobTitle?.id) {
            const response = await trainingJobTitleService.getTrainingJobTitleByJobTitle(selectedJobTitle?.id);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            setTrainingJobTitles(response.trainingJobTitles || []);
        }
    };

    const onFormSubmit = async () => {
        if (!trainingJobTitles) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION);
            return;
        }

        const itensToAdd = trainingJobTitles.filter((item) => !item.id || item.id.trim() === '').map(({ training, ...toAdd }) => toAdd);

        if (!itensToAdd || itensToAdd.length === 0) {
            showWarning(MESSAGES.VALIDATION.EMPTY_COLLECTION_TO_ADD);
            return;
        }

        setIsSubmitting(true);

        const response = await trainingJobTitleService.createTrainingJobTitle(itensToAdd);

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.SAVE_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.SAVED);
        setSelectedTrainingId('');
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const handleAddClick = async () => {
        if (!selectedJobTitle?.id || !selectedTrainingId) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            return;
        }

        const exists = trainingJobTitles.some((item) => item.trainingId === selectedTrainingId);
        if (exists) {
            showWarning(MESSAGES.VALIDATION.ITEM_EXISTS);
        } else {
            const selectedTraining = trainings.find((p) => p.id === selectedTrainingId);

            const newTrainingJobTitle: ITrainingJobTitle = {
                jobTitleId: selectedJobTitle?.id,
                trainingId: selectedTrainingId,
                rowVersion: 1,
                status: StatusEnum.Active,
                training: selectedTraining,
            };

            setTrainingJobTitles((prev) => [...prev, newTrainingJobTitle]);
        }
    };

    const handleDeleteClick = async (trainingJobTitle: ITrainingJobTitle) => {
        if (!trainingJobTitle.id) {
            const filteredList = trainingJobTitles.filter((item) => item.trainingId !== trainingJobTitle.trainingId);
            setTrainingJobTitles(filteredList);
        } else {
            setIsSubmitting(true);

            const response = await trainingJobTitleService.deleteTrainingJobTitle(trainingJobTitle.id);

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.ACTION.DELETED);
            fetchTrainingJobTitle();
        }
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size="xl" isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id="training-job-title-modal">Manutenção de treinamentos da função</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor="primary">
                    <CardTabItem id="tab-item-1" title={selectedJobTitle?.name || 'Manutenção de treinamentos da função'} icon="School">
                        <div className="row g-4">
                            <div className="col-lg-12 col-md-12 col-sm-12">
                                <InputGroup isWrap>

                                    <FormGroup
                                        id='trainings'
                                        label='Treinamentos'
                                        isFloating
                                    >
                                        <Select
                                            ariaLabel="Escolha um treinamento"
                                            list={[
                                                { value: '', text: 'Selecione um treinamento' },
                                                ...trainings.map((training) => ({
                                                    value: training.id,
                                                    text: `${training.name}`, // TODO Checar se precisa converter toString()
                                                })),
                                            ]}
                                            onChange={(e: { target: { value: any } }) => {
                                                if (e.target.value) {
                                                    setSelectedTrainingId(e.target.value);
                                                }
                                            }}
                                        />

                                    </FormGroup>

                                    <Button icon="AddCircleOutline" color="primary" onClick={handleAddClick}>
                                        Adicionar
                                    </Button>
                                </InputGroup>
                            </div>

                            <div className="col-lg-12 col-md-12 col-sm-12 table-responsive ">
                                <table className="table table-modern table-hover">
                                    <thead>
                                        <tr>
                                            <th>Nome</th>
                                            <th>Validade (Dias)</th>
                                            <td aria-labelledby="Actions" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trainingJobTitles.map((item: ITrainingJobTitle) => (
                                            <tr key={item.id}>
                                                <td>{item.training?.name}</td>
                                                <td>{item.training?.validity || 0}</td>
                                                <td className="text-end">
                                                    <Button color="dark" isLight icon="Delete" onClick={() => handleDeleteClick(item)} aria-label="Remover" />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className="bg-transparent">
                <CardFooterRight>
                    <Button color="success" className="w-100" onClick={onFormSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};
